import type {
  ScenarioPackage,
  SentenceEntry,
  TaskSimulationObjective,
  VocabularyEntry,
} from "@mobile/runtime/types";
import { sentenceBelongsToIntent } from "@mobile/runtime/intentSentenceMembership";

import { mergeWebScenarioOverlay } from "./webScenarioOverlay";

const tutorialAudioModules = import.meta.glob<string>(
  "../../../../mobile/app/src/audio/tutorialAudioAssets/audio/**/*.mp3",
  { eager: true, query: "?url", import: "default" },
);

const tutorialAudioByRef = new Map<string, string>();

for (const [modulePath, url] of Object.entries(tutorialAudioModules)) {
  const marker = "/tutorialAudioAssets/";
  const markerIndex = modulePath.indexOf(marker);
  if (markerIndex === -1) {
    continue;
  }
  tutorialAudioByRef.set(modulePath.slice(markerIndex + marker.length), url);
}

interface BootstrapPackageEntry {
  path: string;
  payload: unknown;
}

interface BootstrapLanguagePackage {
  entries: BootstrapPackageEntry[];
}

interface BootstrapDocument {
  packages: Record<string, BootstrapLanguagePackage>;
  selections: Record<string, {
    intentId: string;
    vocabularyId: string;
    objectiveId: string;
    audioRefs: string[];
  }>;
}

let tutorialBootstrapPromise: Promise<BootstrapDocument> | null = null;

async function loadTutorialBootstrap(): Promise<BootstrapDocument> {
  tutorialBootstrapPromise = tutorialBootstrapPromise || import(
    "@mobile/runtime/tutorialBootstrapContent.generated.json"
  ).then((module) => module.default as BootstrapDocument);
  return tutorialBootstrapPromise;
}

export async function loadTutorialScenario(
  targetLanguage: string,
  supportLanguage?: string,
): Promise<ScenarioPackage> {
  const document = await loadTutorialBootstrap();
  const languagePackage = document.packages[targetLanguage] || document.packages.en;
  const overlayPrefix = supportLanguage && supportLanguage !== targetLanguage
    ? `content/locale_overlays/runtime/${supportLanguage}/${targetLanguage}/`
    : null;
  const scenarioEntries = languagePackage?.entries.filter((entry) => (
    Boolean(entry.payload)
    && typeof entry.payload === "object"
    && (entry.payload as { contentType?: unknown }).contentType === "scenarioPackage"
  )) || [];
  const baseEntry = scenarioEntries.find((entry) => (
    entry.path.startsWith(`content/runtime/${targetLanguage}/`)
  )) || scenarioEntries[0];
  const overlayEntry = overlayPrefix
    ? scenarioEntries.find((entry) => entry.path.startsWith(overlayPrefix))
    : null;

  if (!baseEntry) {
    throw new Error(`tutorial_scenario_missing:${targetLanguage}`);
  }
  const baseScenario = baseEntry.payload as ScenarioPackage;
  const scenario = mergeWebScenarioOverlay(
    baseScenario,
    overlayEntry ? overlayEntry.payload as Partial<ScenarioPackage> : null,
    supportLanguage || targetLanguage,
  );
  const selection = document.selections[baseScenario.targetLanguage || targetLanguage];
  if (!selection) throw new Error(`tutorial_selection_missing:${targetLanguage}`);
  const availableRefs = new Set(selection.audioRefs);
  for (const audioRef of availableRefs) {
    if (!resolveTutorialAudioUrl(audioRef)) {
      throw new Error(`tutorial_audio_missing:${audioRef}`);
    }
  }
  const vocabulary = scenario.vocabularyLibrary?.find((entry) => entry.vocabularyId === selection.vocabularyId);
  const mission = scenario.taskStructure.simulationObjectives?.find((entry) => entry.objectiveId === selection.objectiveId);
  const intent = scenario.intentSystem?.find((entry) => entry.intentId === selection.intentId);
  if (!vocabulary?.audioRef || !availableRefs.has(vocabulary.audioRef) || !mission || !intent) {
    throw new Error(`tutorial_starter_selection_invalid:${targetLanguage}`);
  }
  const sentenceLibrary = scenario.sentenceLibrary.filter((entry) => (
    Boolean(entry.audioRef && availableRefs.has(entry.audioRef))
  ));
  for (const sentenceId of [mission.sentenceId, mission.systemLeadSentenceId, mission.systemReplySentenceId].filter(Boolean)) {
    if (!sentenceLibrary.some((entry) => entry.sentenceId === sentenceId)) {
      throw new Error(`tutorial_mission_audio_missing:${sentenceId}`);
    }
  }
  return {
    ...scenario,
    intentSystem: [intent],
    sentenceLibrary,
    vocabularyLibrary: [vocabulary],
    taskStructure: { ...scenario.taskStructure, simulationObjectives: [mission] },
  };
}

export function getSentenceById(
  scenario: ScenarioPackage,
  sentenceId?: string | null,
): SentenceEntry | null {
  if (!sentenceId) {
    return null;
  }
  return scenario.sentenceLibrary.find((sentence) => sentence.sentenceId === sentenceId) || null;
}

export function getTutorialPracticeSentences(scenario: ScenarioPackage): SentenceEntry[] {
  return scenario.sentenceLibrary
    .filter((sentence) => (
      Boolean(sentence.text?.trim())
      && sentenceBelongsToIntent(sentence, scenario.intentSystem?.[0]?.intentId || "")
    ))
    .slice(0, 8);
}

export function getTutorialVocabulary(scenario: ScenarioPackage): VocabularyEntry[] {
  return (scenario.vocabularyLibrary || [])
    .filter((entry) => Boolean((entry.display || entry.lemma || "").trim()))
    .slice(0, 8);
}

export function getTutorialMissions(scenario: ScenarioPackage): TaskSimulationObjective[] {
  return (scenario.taskStructure.simulationObjectives || []).slice(0, 8);
}

export function resolveTutorialAudioUrl(audioRef?: string | null): string | null {
  if (!audioRef) {
    return null;
  }
  return tutorialAudioByRef.get(audioRef) || null;
}
